import type { Question } from "@ui/components/SobQuiz/SobQuiz.types";

export type QuizAnswer = {
  type: string;
  answer_id: string;
  question_id: string;
  reference_name: string;
  local_translation: string;
};

type QuizEvent =
  | { type: "CONSENT_GIVEN" }
  | { type: "CONSENT_DENIED" }
  | { type: "ANSWER"; payload: QuizAnswer }
  | { type: "COMPLETE" };

export type QuizState =
  | { value: "consent"; context: QuizContext }
  | { value: "declined"; context: QuizContext }
  | { value: "quiz"; context: QuizContext & { currentTransition: Question } }
  | { value: "completion"; context: QuizContext }
  | { value: "error"; context: QuizContext & { error: string } };

export interface QuizContext {
  answers: QuizAnswer[];
  currentTransition: Question | null;
  consentGiven?: boolean;
  error?: string;
  nextState?: string;
}

interface QuizStateDefinition {
  actions: {
    onEnter?: (context: QuizContext) => void | Promise<void>;
    onExit?: (context: QuizContext) => void | Promise<void>;
  };
  transitions: Record<
    string,
    {
      target: string;
      action?: (context: QuizContext, payload?: any) => void | Promise<void>;
    }
  >;
}

export const createQuizMachine = () => {
  const states: Record<string, QuizStateDefinition> = {
    consent: {
      actions: {
        onEnter: () => {
          console.info("[SOB] Showing consent form");
        },
      },
      transitions: {
        CONSENT_GIVEN: {
          target: "quiz",
          action: (context) => {
            context.consentGiven = true;
          },
        },
        CONSENT_DENIED: {
          target: "declined",
          action: (context) => {
            context.consentGiven = false;
          },
        },
      },
    },
    declined: {
      actions: {
        onEnter: () => {
          console.info("[SOB] User declined consent");
        },
      },
      transitions: {},
    },
    quiz: {
      actions: {
        onEnter: () => {
          console.info("[SOB] Entered quiz state");
        },
      },
      transitions: {
        CONSENT_GIVEN: {
          target: "quiz",
          action: (context) => {
            context.consentGiven = true;
          },
        },
        ANSWER: {
          target: "quiz",
          action: (context, payload: QuizAnswer) => {
            context.answers.push({
              question_id: payload.question_id,
              answer_id: payload.answer_id,
              type: payload.type,
              reference_name: payload.reference_name,
              local_translation: payload.local_translation,
            });
          },
        },
        COMPLETE: {
          target: "completion",
        },
      },
    },
    completion: {
      actions: {
        onEnter: async (context) => {
          try {
            console.info("[SOB] Quiz completed", context.answers);
          } catch (error) {
            if (error instanceof Error) {
              context.error = error.message;
              context.nextState = "error";
            }
          }
        },
      },
      transitions: {},
    },
    error: {
      actions: {},
      transitions: {},
    },
  };

  const machine = {
    value: "",
    context: {
      answers: [],
      currentTransition: null,
    } as QuizContext,

    initialize(hasConsent: boolean) {
      this.value = hasConsent ? "quiz" : "consent";

      if (hasConsent) {
        this.context.consentGiven = true;
        states[this.value].actions.onEnter?.(this.context);
      }
    },

    async transition(event: QuizEvent): Promise<QuizState> {
      const currentState = states[this.value];
      const transition = currentState.transitions[event.type];

      if (!transition) {
        throw new Error(`No transition found for event ${event.type} in state ${this.value}`);
      }

      if (transition.action) {
        const payload = "payload" in event ? event.payload : undefined;
        await transition.action(this.context, payload);

        this.value = this.context.nextState || transition.target;
        this.context.nextState = undefined;
      } else {
        this.value = transition.target;
      }

      const newState = states[this.value];
      await newState.actions.onEnter?.(this.context);

      return {
        value: this.value,
        context: this.context,
      } as QuizState;
    },
  };

  return machine;
};
